import React, { useState, useEffect } from "react"; // Import useEffect
import styles from "./.FilterBoxComponents.module.css";
import revenueStyles from "./CompanyRevenueFilter.module.css"; // Reusing styles
import { formatWithCommas } from "functions/fields"; // Assuming this utility exists

const FundingFilter = ({ fundingAmount, setFundingAmount }) => {
  // Helper function to derive local state format from the fundingAmount prop
  const getLocalStateFromProp = (propValue) => {
    // Default to [null, null] if prop isn't a valid array initially
    const validProp = Array.isArray(propValue) && propValue.length === 2 ? propValue : [null, null];
    return validProp.map(e => (e === null ? "" : String(e)));
  };

  // Initialize local state using the helper, handling potential undefined/null initial prop
  const [localFunding, setLocalFunding] = useState(() => getLocalStateFromProp(fundingAmount));

  // Effect to synchronize local state when the fundingAmount prop changes from the parent
  useEffect(() => {
    const updatedLocalFunding = getLocalStateFromProp(fundingAmount);
    setLocalFunding((prev) => {
      const currentLocal = Array.isArray(prev) ? prev : ["", ""];
      if (updatedLocalFunding[0] === currentLocal[0] && updatedLocalFunding[1] === currentLocal[1]) {
        return prev;
      }
      return updatedLocalFunding;
    });
    // Dependency array: run this effect when the 'fundingAmount' prop changes
  }, [fundingAmount]);

  const handleChange = (event, index) => {
    const raw = event.target.value.replace(/,/g, "");
    const sanitized = raw.replace(/[^\d]/g, ""); // Keep only digits
    // Ensure localFunding is treated as an array even if it becomes undefined momentarily
    const currentLocal = Array.isArray(localFunding) ? localFunding : ["", ""];
    const updated = [...currentLocal];
    updated[index] = sanitized;
    setLocalFunding(updated);
  };

  // Updated handleBlur incorporating both fixes
  const handleBlur = () => {
    // Ensure localFunding is treated as an array
    const currentLocal = Array.isArray(localFunding) ? localFunding : ["", ""];
    let [minStr, maxStr] = currentLocal;

    // Default to empty string if undefined/null before checking emptiness
    minStr = minStr ?? "";
    maxStr = maxStr ?? "";

    const min = minStr === "" ? null : Number(minStr);
    const max = maxStr === "" ? null : Number(maxStr);

    // Ensure no NaN makes it through
    const updatedMin = isNaN(min) ? null : min;
    const updatedMax = isNaN(max) ? null : max;

    let finalMin = updatedMin;
    let finalMax = updatedMax;

    if (updatedMin !== null && updatedMax !== null && updatedMax < updatedMin) {
        // If max < min, set max = min to ensure validity
        finalMax = updatedMin;
    }

    // --- FIX 2: Determine correct parent state format (empty array vs [null, null]) ---
    let nextParentState;
    if (finalMin === null && finalMax === null) {
        nextParentState = []; // Use empty array if both are null/cleared
    } else {
        nextParentState = [finalMin, finalMax];
    }

    // Get current parent state, default to [] if not an array for comparison
    const currentParentState = Array.isArray(fundingAmount) ? fundingAmount : [];

    // Check if the state actually needs to change by comparing length and elements
    let needsUpdate = false;
    if (nextParentState.length !== currentParentState.length) {
        needsUpdate = true;
    } else if (nextParentState.length === 2) {
        // Only compare elements if both arrays have length 2
        if (nextParentState[0] !== currentParentState[0] || nextParentState[1] !== currentParentState[1]) {
            needsUpdate = true;
        }
    }
    // If both lengths are 0, needsUpdate remains false (no change needed)

    // Update parent state only if the calculated state is different
    if (needsUpdate) {
        setFundingAmount(nextParentState);
    }
    // --- END FIX 2 ---


    // Update local state based on the final validated/adjusted values, ensuring strings
    const nextLocalState = [
        finalMin === null ? "" : String(finalMin),
        finalMax === null ? "" : String(finalMax)
    ];
    // Only update local state if it differs, prevents re-render if blur didn't change effective value
    if(nextLocalState[0] !== currentLocal[0] || nextLocalState[1] !== currentLocal[1]) {
       setLocalFunding(nextLocalState);
    }
  };


  // Ensure localFunding is always an array for rendering
  const displayFunding = Array.isArray(localFunding) ? localFunding : ["", ""];

  return (
    <div className={styles.accordionContent}>
      <label>Company funding is between:</label>
      <div className={revenueStyles.revenueRange}>
        <div className={revenueStyles.revenueInputContainer}>
          <label className={revenueStyles.minLabel}>Min</label>
          <label className={revenueStyles.dollarSign}>$</label>
          <input
            type="text"
            // Use displayFunding which is guaranteed to be an array
            value={displayFunding[0] === "" ? "" : formatWithCommas(displayFunding[0])}
            onChange={(e) => handleChange(e, 0)}
            onBlur={handleBlur}
            placeholder="No min"
          />
        </div>
        <div className={revenueStyles.revenueInputContainer}>
          <label className={revenueStyles.maxLabel}>Max</label>
          <label className={revenueStyles.dollarSign}>$</label>
          <input
            type="text"
            // Use displayFunding which is guaranteed to be an array
            value={displayFunding[1] === "" ? "" : formatWithCommas(displayFunding[1])}
            onChange={(e) => handleChange(e, 1)}
            onBlur={handleBlur}
            placeholder="No max"
          />
        </div>
      </div>
    </div>
  );
};

export default FundingFilter;